import { useState } from "react";
import { Video, Image as ImageIcon, X, ZoomIn, ZoomOut } from "lucide-react";
// TODO: Re-enable when annotation feature is ready
// import { Trash2, Pencil } from "lucide-react";
// import { ImageAnnotationModal } from "../../../ImageAnnotationModal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { Button } from "@/components/ui/button";
import {
  updateConsultationActions,
  setImageDocDetails,
  setIncrementImageDocCompleted,
} from "@/dispatchers/patientDispatchers";
import { ActionType } from "@/enum/ActionType";

interface ImageDocItem {
  details?: {
    fileContent?: string;
    title?: string;
    fileName?: string;
    isVideo?: boolean;
    bodyArea?: string;
    patientId?: string;
  } | null;
  displayTimestamp?: string;
  recordedAt?: string;
  [key: string]: any;
}

// ── Simple image viewer modal ─────────────────────────────────────────────────
function ImageViewerModal({
  open,
  onClose,
  imageSrc,
  title,
  isDarkMode,
}: {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  title: string;
  isDarkMode?: boolean;
}) {
  const [zoom, setZoom] = useState(1);

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      setZoom(1);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 [&>button]:hidden ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
      >
        <VisuallyHidden>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>
        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <span
            className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {title}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
              disabled={zoom <= 0.5}
              className={`h-8 w-8 p-0 ${isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span
              className={`text-xs min-w-[44px] text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoom((z) => Math.min(z + 0.25, 3))}
              disabled={zoom >= 3}
              className={`h-8 w-8 p-0 ${isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className={`h-8 w-8 p-0 ml-1 ${isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {/* Image */}
        <div
          className={`flex-1 overflow-auto flex items-center justify-center p-4 ${isDarkMode ? "bg-gray-950" : "bg-gray-50"}`}
        >
          <img
            src={imageSrc}
            alt={title}
            className="max-w-full max-h-full object-contain transition-transform duration-200"
            style={{ transform: `scale(${zoom})` }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export const ImageDocumentActionItems = ({
  items,
  isDarkMode,
  handleImageDocClick,
  consultationId,
}: {
  items: ImageDocItem[];
  isDarkMode: boolean;
  handleImageDocClick: (details: any) => void;
  consultationId: string;
}) => {
  const validItems = items.filter((item) => item.details !== null);

  const sortedItems = [...validItems].sort((a, b) => {
    const dateA = new Date(a.displayTimestamp || a.recordedAt || 0).getTime();
    const dateB = new Date(b.displayTimestamp || b.recordedAt || 0).getTime();
    return dateA - dateB;
  });

  // ── View modal state ───────────────────────────────────────────────────────
  const [viewModal, setViewModal] = useState<{
    open: boolean;
    imageSrc: string;
    title: string;
  } | null>(null);

  // TODO: Re-enable when annotation feature is ready
  // Local state: map index → overridden base64 (after annotation save)
  // const [annotatedPreviews, setAnnotatedPreviews] = useState<Record<number, string>>({});
  // Store item in modal state so it's accessible inside onSave
  // const [annotationModal, setAnnotationModal] = useState<{
  //   open: boolean;
  //   index: number;
  //   imageUrl: string;
  //   title: string;
  //   item: ImageDocItem;
  // } | null>(null);

  const getImageSrc = (item: ImageDocItem, index?: number): string | null => {
    // TODO: Re-enable annotated preview lookup when annotation is ready
    // if (annotatedPreviews[index]) return annotatedPreviews[index];
    const fc = item.details?.fileContent;
    if (!fc) return null;
    if (fc.startsWith("data:")) return fc;
    return `data:image/jpeg;base64,${fc}`;
  };

  const handleThumbnailClick = (item: ImageDocItem, index: number) => {
    if (item.details?.isVideo) {
      handleImageDocClick(item.details);
      return;
    }
    const src = getImageSrc(item, index);
    if (!src) return;

    // Open simple viewer
    setViewModal({
      open: true,
      imageSrc: src,
      title:
        item.details?.title || item.details?.fileName || `Image ${index + 1}`,
    });

    // TODO: Re-enable when annotation feature is ready
    // setAnnotationModal({
    //   open: true,
    //   index,
    //   imageUrl: src,
    //   title: item.details?.title || item.details?.fileName || `Image ${index + 1}`,
    //   item,
    // });
  };

  // TODO: Re-enable when annotation feature is ready
  // const handleAnnotationSave = async (annotatedImageBase64: string) => {
  //   if (!annotationModal) return;
  //   const { item, index } = annotationModal;
  //   const base64Data = annotatedImageBase64.includes(",")
  //     ? annotatedImageBase64.split(",")[1]
  //     : annotatedImageBase64;
  //   setAnnotatedPreviews((prev) => ({ ...prev, [index]: annotatedImageBase64 }));
  //   setAnnotationModal(null);
  //   const annotatedPayload = {
  //     patientId: item.details?.patientId,
  //     title: `${item.details?.title ?? "Image"} (annotated)`,
  //     fileName: `${item.details?.title ?? "Image"}_annotated.jpeg`,
  //     description: `Annotated: ${item.details?.title ?? "Image"}`,
  //     size: `${Math.round((base64Data.length * 3) / 4 / 1024)} KB`,
  //     status: 1,
  //     fileContent: base64Data,
  //     consultationId,
  //     bodyArea: item.details?.bodyArea,
  //     isVideo: false,
  //     recordedAt: new Date().toISOString(),
  //   };
  //   try {
  //     await updateConsultationActions({
  //       consultationId,
  //       payload: {
  //         actionName: ActionType[13],
  //         actionNumber: "13",
  //         isPerformed: true,
  //         payload: JSON.stringify(annotatedPayload),
  //       },
  //     });
  //     setImageDocDetails(annotatedPayload);
  //     setIncrementImageDocCompleted(true);
  //   } catch (err) {
  //     console.error("Failed to save annotated image:", err);
  //   }
  // };

  return (
    <div className="px-3 pb-3">
      <div className="flex flex-wrap gap-2">
        {sortedItems.map((item, index) => {
          const imgSrc = getImageSrc(item, index);
          const isVideo = item.details?.isVideo;
          const title =
            item.details?.title ||
            item.details?.fileName ||
            `Item ${index + 1}`;

          return (
            <div
              key={index}
              className="group relative"
              style={{ width: "80px" }}
            >
              {/* Media Thumbnail */}
              <div
                onClick={() => handleThumbnailClick(item, index)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer hover:border-blue-500 hover:shadow-lg ${
                  isDarkMode ? "border-gray-600" : "border-gray-200"
                }`}
                style={{ width: "80px", height: "80px" }}
                title={isVideo ? "Click to preview video" : "Click to view"}
              >
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : isVideo ? (
                  <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                    <Video className="w-8 h-8 text-gray-400" />
                  </div>
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}

                {/* Hover overlay — video only */}
                {/* TODO: Re-enable pencil overlay for images when annotation is ready */}
                {/* {!isVideo && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Pencil className="w-4 h-4 text-white" />
                  </div>
                )} */}
                {isVideo && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Video className="w-6 h-6 text-white" />
                  </div>
                )}

                {/* Video badge */}
                {isVideo && (
                  <div className="absolute top-0.5 left-0.5 bg-black/60 rounded px-1 flex items-center gap-0.5">
                    <Video className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </div>

              {/* Title */}
              <p
                className={`text-xs mt-1 text-center truncate ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
                title={title}
                style={{ width: "80px" }}
              >
                {title}
              </p>
            </div>
          );
        })}
      </div>

      {/* Image viewer modal */}
      {viewModal && (
        <ImageViewerModal
          open={viewModal.open}
          onClose={() => setViewModal(null)}
          imageSrc={viewModal.imageSrc}
          title={viewModal.title}
          isDarkMode={isDarkMode}
        />
      )}

      {/* TODO: Re-enable when annotation feature is ready */}
      {/* {annotationModal && (
        <ImageAnnotationModal
          open={annotationModal.open}
          onOpenChange={(open) => { if (!open) setAnnotationModal(null); }}
          imageUrl={annotationModal.imageUrl}
          imageTitle={annotationModal.title}
          onSave={handleAnnotationSave}
        />
      )} */}
    </div>
  );
};
